import { useMemo } from 'react';
import type { Question } from '../api/types';
import { useTranslation } from '../i18n';
import {
  BooleanResponse,
  DateResponse,
  MultiResponse,
  NumberResponse,
  SelectResponse,
  TextResponse,
} from './responses';
import './ChatInput.css';

export interface ChatInputSubmit {
  message?: string;
  answer?: { field: string; value: unknown };
}

interface Props {
  question: Question | null;
  disabled: boolean;
  onSubmit: (result: ChatInputSubmit) => void;
}

/** The single input area below the chat. Delegates to a response component
 *  per `question.type`; falls back to free-text when `question` is null.
 *
 *  Component selection per field:
 *   text    → TextResponse  (full_name, address, city, state, zip, county, ssn_last_four)
 *   select  → SelectResponse — chips for short option sets, stack for longer ones
 *             (preferred_language, reason_for_visit, employment_status, education_level,
 *              ethnicity, sex_at_birth, gender, disability_status, veteran_status)
 *   multi   → MultiResponse  (race)
 *   boolean → BooleanResponse — staged for consent_agreement, immediate for the rest
 *             (msfw_status, new_american_status, rapid_response_attendance, consent_agreement)
 *   date    → DateResponse   (dob)
 *   number  → NumberResponse (future income/hours fields)
 *
 *  Layout hints (layout, inputHints, staged, dateMin/Max, numberMin/Max) flow from
 *  the program config through the Worker's Question block — this component just
 *  passes them through. */
export function ChatInput({ question, disabled, onSubmit }: Props) {
  const { t } = useTranslation();

  // Stable key forces a fresh mount + autoFocus whenever the question
  // field or type changes. Keeps keyboard users aligned with the visible
  // state (no stale draft from a prior question).
  const swapKey = useMemo(
    () => `${question?.field ?? 'text'}::${question?.type ?? 'text'}`,
    [question?.field, question?.type],
  );

  if (question === null) {
    return (
      <div className="cf-chat-input">
        <TextResponse
          key={swapKey}
          label={t('chat.input_placeholder')}
          hideLabel
          multiline
          disabled={disabled}
          autoFocus
          placeholder={t('chat.input_placeholder')}
          submitLabel={t('chat.send')}
          onSubmit={(message) => onSubmit({ message })}
        />
      </div>
    );
  }

  const submitLabel = t('chat.send');
  const common = {
    disabled,
    autoFocus: true,
    submitLabel,
    key: swapKey,
  };

  switch (question.type) {
    case 'text':
      return (
        <div className="cf-chat-input">
          <TextResponse
            {...common}
            label={question.label}
            multiline={false}
            autoComplete={question.inputHints?.autoComplete}
            inputMode={question.inputHints?.inputMode}
            maxLength={question.inputHints?.maxLength}
            onSubmit={(value) =>
              onSubmit({ answer: { field: question.field, value } })
            }
          />
        </div>
      );

    case 'select':
      return (
        <div className="cf-chat-input">
          <SelectResponse
            {...common}
            label={question.label}
            layout={question.layout}
            options={question.options}
            onSubmit={(value) =>
              onSubmit({ answer: { field: question.field, value } })
            }
          />
        </div>
      );

    case 'multi':
      return (
        <div className="cf-chat-input">
          <MultiResponse
            {...common}
            label={question.label}
            options={question.options}
            onSubmit={(value) =>
              onSubmit({ answer: { field: question.field, value } })
            }
          />
        </div>
      );

    case 'boolean':
      return (
        <div className="cf-chat-input">
          <BooleanResponse
            {...common}
            label={question.label}
            // For non-staged boolean (msfw, new_american, rapid_response) the label
            // is shown in the chat bubble by the LLM, not repeated in the control.
            // For staged (consent_agreement) we show it so the user knows what they're confirming.
            hideLabel={!question.staged}
            stage={question.staged}
            yesLabel={t('chat.question.boolean.yes')}
            noLabel={t('chat.question.boolean.no')}
            submitLabel={question.staged ? t('chat.question.boolean.confirm') : submitLabel}
            onSubmit={(value) =>
              onSubmit({ answer: { field: question.field, value } })
            }
          />
        </div>
      );

    case 'date':
      return (
        <div className="cf-chat-input">
          <DateResponse
            {...common}
            label={question.label}
            min={question.dateMin}
            max={question.dateMax}
            onSubmit={(value) =>
              onSubmit({ answer: { field: question.field, value } })
            }
          />
        </div>
      );

    case 'number':
      return (
        <div className="cf-chat-input">
          <NumberResponse
            {...common}
            label={question.label}
            min={question.numberMin}
            max={question.numberMax}
            step={question.step}
            prefix={question.prefix}
            suffix={question.suffix}
            onSubmit={(value) =>
              onSubmit({ answer: { field: question.field, value } })
            }
          />
        </div>
      );
  }
}
